/**
 * Aviso en la reunión cuando la conversación delata los nombres («Hola Diego»,
 * «Perfecto, Carla»): sin esto las sugerencias solo aparecían dentro del diálogo
 * de hablantes y las notas quedaban con «Participante 1». Un clic los aplica a
 * la transcripción, las notas y lo que se exporte.
 */
import { useState } from 'react'
import { speakerDisplayName, type RecordingDetail } from '@eco/shared'
import { useApp, useToast, errorText } from './AppContext'
import { speakerNameHints } from '../lib/speakerHints'
import { IconClose } from './icons'

const cerradoKey = (id: string) => `eco.nombres.${id}`

function cerrado(id: string): boolean {
  try {
    return localStorage.getItem(cerradoKey(id)) === '1'
  } catch {
    return false
  }
}

export function NameHints({
  detail,
  onReview,
  onChanged
}: {
  detail: RecordingDetail
  onReview: () => void
  onChanged: () => Promise<void>
}) {
  const { api } = useApp()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [oculto, setOculto] = useState(() => cerrado(detail.recording.id))
  if (oculto || detail.recording.estado !== 'completed') return null

  // Solo sugerencias para voces sin nombre y con un nombre que nadie más ya tiene
  const usados = new Set(detail.speakers.map((s) => s.nombre).filter(Boolean))
  const porNombre = new Map(
    detail.speakers.map((s) => [speakerDisplayName(s.etiqueta, s.nombre), s])
  )
  const sugerencias = speakerNameHints(detail.segments)
    .map((h) => ({ ...h, sp: porNombre.get(h.speaker) }))
    .filter((h) => h.sp && !h.sp.nombre && !usados.has(h.nombre))
  if (sugerencias.length === 0) return null

  function cerrar() {
    try {
      localStorage.setItem(cerradoKey(detail.recording.id), '1')
    } catch {
      // Sin almacenamiento: se oculta solo en esta visita
    }
    setOculto(true)
  }

  async function aplicar() {
    setBusy(true)
    try {
      for (const h of sugerencias) await api.renameSpeaker(detail.recording.id, h.sp!.id, h.nombre)
      await onChanged()
      toast('Nombres guardados: la transcripción y las notas ya los muestran')
      cerrar()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  const frase = sugerencias.map((h) => `${h.speaker} es ${h.nombre}`)
  return (
    <div className="notice row wrap" style={{ marginBottom: 14 }}>
      <span className="grow">
        Por la conversación, parece que{' '}
        {frase.length > 1 ? `${frase.slice(0, -1).join(', ')} y ${frase.at(-1)}` : frase[0]}.
      </span>
      <button className="btn sm primary" disabled={busy} onClick={() => void aplicar()}>
        {busy ? 'Guardando…' : sugerencias.length > 1 ? 'Usar estos nombres' : 'Usar el nombre'}
      </button>
      <button className="btn sm ghost" disabled={busy} onClick={onReview}>
        Revisar
      </button>
      <button className="btn sm ghost" disabled={busy} onClick={cerrar} aria-label="Descartar">
        <IconClose />
      </button>
    </div>
  )
}
